import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { executionsAPI, automationsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import {
  PlayCircle,
  Download,
  Calendar,
  BarChart3,
  CheckCircle,
  XCircle,
  Loader2,
  Ban,
  Zap,
} from 'lucide-react';
import { formatDate, formatDuration, getStatusColor, formatTimeSaved } from '@/shared/lib/utils';
import type { Execution, ExecutionStatus, ExecutionListParams, Automation } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { FilterAccordion } from '@travellegacy/nexops-design-system';
import { Pagination } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { useDateDebounce } from '@/shared/hooks/useDebounce';
import { AutomationTypeBadge } from '../components/AutomationTypeBadge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';

export default function Executions() {
  const [statusFilter, setStatusFilter] = useState<ExecutionStatus | 'all'>('all');
  const [automationFilter, setAutomationFilter] = useState<string>('all');
  const [dateInputValue, setDateInputValue] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Debounce date filter
  const dateFilter = useDateDebounce(dateInputValue);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, automationFilter, dateFilter]);

  // Fetch automations for filter
  const { data: automationsData } = useQuery({
    queryKey: ['automations'],
    queryFn: async () => {
      const response = await automationsAPI.list({ includeStats: false });
      return response.data.data as Automation[];
    },
  });

  const automations = automationsData || [];

  // Fetch executions
  const { data, isLoading } = useQuery({
    queryKey: ['executions', statusFilter, automationFilter, dateFilter, page],
    queryFn: async () => {
      const params: ExecutionListParams = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (automationFilter !== 'all') {
        params.automationId = automationFilter;
      }
      if (dateFilter && dateFilter.length === 10) {
        const [year, month, day] = dateFilter.split('-').map(Number);
        const startDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        const endDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        params.startDate = startDate.toISOString();
        params.endDate = endDate.toISOString();
      }
      const response = await executionsAPI.list(params);
      return response.data;
    },
  });

  const executions = (data?.data as Execution[]) || [];
  const total = data?.pagination?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  // Fetch stats with filters
  const { data: stats } = useQuery({
    queryKey: ['execution-stats', automationFilter, dateFilter],
    queryFn: async () => {
      const params: { automationId?: string; startDate?: string; endDate?: string } = {};

      if (automationFilter !== 'all') {
        params.automationId = automationFilter;
      }

      if (dateFilter && dateFilter.length === 10) {
        const [year, month, day] = dateFilter.split('-').map(Number);
        const startDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        const endDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        params.startDate = startDate.toISOString();
        params.endDate = endDate.toISOString();
      }

      const response = await executionsAPI.stats(params);
      return response.data.data;
    },
  });

  // Tabs configuration
  const statusTabs = [
    { value: 'all', label: 'Todas', count: stats?.total || 0, color: 'primary' as const },
    { value: 'SUCCESS', label: 'Sucesso', count: stats?.success || 0, color: 'green' as const },
    { value: 'ERROR', label: 'Erro', count: stats?.error || 0, color: 'red' as const },
    { value: 'RUNNING', label: 'Em Execução', count: stats?.running || 0, color: 'blue' as const },
    {
      value: 'CANCELLED',
      label: 'Cancelada',
      count: stats?.cancelled || 0,
      color: 'gray' as const,
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-9 w-48 bg-muted animate-pulse rounded" />
            <div className="h-5 w-96 bg-muted animate-pulse rounded mt-1" />
          </div>
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-5 w-32 bg-muted rounded mb-4" />
              <div className="h-8 w-24 bg-muted rounded mb-2" />
              <div className="h-4 w-full bg-muted rounded" />
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-card rounded-lg border p-6 animate-pulse">
          <div className="h-6 w-24 bg-muted rounded mb-4" />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-card rounded-lg border animate-pulse">
          <div className="p-6">
            <div className="h-6 w-40 bg-muted rounded mb-4" />
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-16 bg-muted rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Execuções</h1>
          <p className="text-muted-foreground mt-1">Histórico de execuções de automações</p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Filters */}
      <FilterAccordion
        activeFiltersCount={[automationFilter !== 'all' ? 1 : 0, dateFilter ? 1 : 0].reduce(
          (a, b) => a + b,
          0
        )}
        onClearFilters={() => {
          setAutomationFilter('all');
          setDateInputValue('');
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          {/* Automation Filter */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              <Zap className="h-4 w-4 inline mr-1" />
              Automação
            </label>
            <Select
              value={automationFilter}
              onChange={(e) => setAutomationFilter(e.target.value)}
              options={[
                { value: 'all', label: 'Todas as automações' },
                ...automations.map((automation) => ({
                  value: automation.id,
                  label: automation.name,
                })),
              ]}
            />
          </div>

          {/* Date Filter */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              <Calendar className="h-4 w-4 inline mr-1" />
              Data
            </label>
            <Input
              type="date"
              value={dateInputValue}
              onChange={(e) => setDateInputValue(e.target.value)}
              placeholder="Selecione uma data"
            />
          </div>
        </div>
      </FilterAccordion>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-card-foreground">{stats?.total || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Total</p>
              </div>
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats?.success || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Sucesso</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {stats?.error || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Erro</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats?.running || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Em Execução</p>
              </div>
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {stats?.cancelled || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Cancelada</p>
              </div>
              <Ban className="h-8 w-8 text-gray-600 dark:text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Executions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center mb-4">
            <PlayCircle className="h-5 w-5 mr-2" />
            Execuções
          </CardTitle>
          {/* Status Tabs */}
          <TabFilter
            tabs={statusTabs}
            currentTab={statusFilter}
            onChange={(value) => setStatusFilter(value as ExecutionStatus | 'all')}
          />
        </CardHeader>
        <CardContent>
          {executions.length === 0 ? (
            <div className="text-center py-12">
              <PlayCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-card-foreground mb-2">
                Nenhuma execução encontrada
              </h3>
              <p className="text-muted-foreground">
                Tente ajustar os filtros ou aguarde novas execuções
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Automação</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Economia</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map((execution) => (
                    <TableRow key={execution.id}>
                      <TableCell>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-card-foreground">
                              {execution.automation?.name || 'N/A'}
                            </p>
                            {execution.automation?.type && (
                              <AutomationTypeBadge
                                type={execution.automation.type}
                                size="sm"
                                showIcon={false}
                              />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {execution.id.substring(0, 8)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(execution.status)}>
                          {execution.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-card-foreground">
                            {formatDate(execution.startedAt, 'dd/MM/yyyy')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(execution.startedAt, 'HH:mm:ss')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-card-foreground">
                          {execution.duration ? formatDuration(execution.duration) : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                          {execution.metadata &&
                          typeof execution.metadata === 'object' &&
                          'timeSavedMinutes' in execution.metadata
                            ? formatTimeSaved(execution.metadata.timeSavedMinutes as number)
                            : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Link to={`/executions/${execution.id}`}>
                          <Button variant="ghost" size="sm">
                            Ver detalhes
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
